/**
 * KYC 인증 — 2026-10-12(물음 16 ⑤ — 사장님 「거래소에서 회원가입을 하면 KYC 인증을 해야하지? … 인증 성공하면 관리자 승인 없이
 * 회원가입이 완료되고, … 회원가입 화면에서 수동심사요청이라는 버튼을 눌러서 거래소 관리자에 KYC인증 메뉴에서 관리자가 직접 보고 가입
 * 승인을 할 수 있도록 만들어야 돼」, 관리자가 거절할 때 「1」(거절 메일, 그 신청은 닫힘, 처음부터 다시)). 옛 「Sign-up approvals」
 * (EA-08~EA-10 — 모든 가입을 관리자가 승인)를 바꾼다: 보통 가입은 신원 인증이 통과하면 저절로 승인되고, 관리자가 정하는 것은
 * 수동심사(`MANUAL_REVIEW`)뿐이다. 다른 상태는 기록 보기.
 *
 * 계약: XM-30(목록 — 상태 거르기)·XM-31(상세 — 신원 인증·SUMSUB 결과)·XM-123(제출 자료 — 단계)·XM-124(사진 하나 — 볼 때마다
 * 서버 감사)·XM-32·XM-33(수동심사 승인·거절).
 *
 * ⚠ **버튼은 키로 그린다**(명세 0-5). 보기 `signups.view`, 승인·거절 `signups.review` — 가입 승인 키 둘을 이 메뉴의 키로 쓴다
 * (서버 7절 이름표 'KYC verification — …').
 *
 * ⚠ **사진은 누를 때만 불러온다.** 신분증 사진은 개인정보이고 볼 때마다 서버가 감사 기록을 남긴다 — 상세를 연 것만으로 남지
 * 않게 사진마다 [View].
 *
 * ⚠ **거절 창에 사유 칸이 없다**(옛 EA-10 과 같음 — 창구는 사유를 받는다). 영어만(관리자 웹).
 */
import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { approveSignup, fetchSignupDocument, getSignup, getSignupDocuments, listSignups, rejectSignup } from '@/api/admin'
import { useSession } from '@/auth/session'
import { setFlash } from '@/auth/pendingWrite'
import { Button, Card, Code, Labeled, LINK, Maybe, MemberLink, Notice, Row, Tabs, TD, TH, Time } from '@/components/ui'
import { Confirm, submitWrite, useWrite } from '@/components/Confirm'
import { QueryState } from '@/components/QueryState'
import {
  APPROVED_BY_LABEL, KYC_PROVIDER_LABEL, SIGNUP_KYC_STATUS_LABEL, SIGNUP_STATUS_LABEL,
  type KycImage, type KycStep, type SignupApplication, type SignupKyc, type SignupStatus,
} from '@/api/types'

type Filter = SignupStatus | 'ALL'

/** 목록의 거르기 — 처음은 관리자가 할 일(수동심사 대기). */
export const FILTERS: readonly { kind: Filter; label: string }[] = [
  { kind: 'MANUAL_REVIEW', label: 'Manual review requested' },
  { kind: 'KYC_REVIEW', label: 'Waiting for the identity check' },
  { kind: 'KYC_REJECTED', label: 'Identity check not approved' },
  { kind: 'APPROVED', label: 'Approved' },
  { kind: 'REJECTED', label: 'Rejected' },
  { kind: 'ALL', label: 'All' },
]

export const PAGE_NOTE = 'A sign-up is approved by itself when its identity check (SUMSUB) passes. When SUMSUB finally '
  + 'rejects it, the applicant can request a manual review on the sign-up screen — those wait here for an admin to look at '
  + 'the documents and approve or reject the sign-up.'

/* ── 목록 (XM-30) ─────────────────────────────────────────────────── */

export function KycVerificationPage() {
  const [filter, setFilter] = useState<Filter>('MANUAL_REVIEW')
  const q = useQuery({
    queryKey: ['signups', filter],
    queryFn: () => listSignups(filter === 'ALL' ? {} : { status: filter }),
  })
  const rows = q.data?.applications ?? []

  return (
    <Card title="KYC verification">
      <p className="mb-4 text-xs text-slate-500">{PAGE_NOTE}</p>
      <Tabs tabs={FILTERS} active={filter} onSelect={setFilter} />
      <QueryState isPending={q.isPending} error={q.error} empty={rows.length === 0}
        emptyText={filter === 'MANUAL_REVIEW' ? 'No manual review is waiting.' : 'No sign-up applications.'}>
        <table className="w-full text-sm">
          <thead><tr>
            <th className={TH}>ID</th><th className={TH}>Email</th><th className={TH}>Status</th>
            <th className={TH}>Identity check</th><th className={TH}>Applied at</th>
            <th className={TH}>Manual review requested at</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.applicationId}>
                <td className={TD}>
                  <Link className={LINK} to={`/kyc-verification/${r.applicationId}`}>{r.loginId}</Link>
                </td>
                <td className={TD}><Maybe value={r.email} /></td>
                <td className={TD}><Labeled labels={SIGNUP_STATUS_LABEL} code={r.status} /></td>
                <td className={TD}><KycSummary kyc={r.kyc} /></td>
                <td className={TD}><Time ms={r.createdAt} /></td>
                <td className={TD}><Time ms={r.manualReviewRequestedAt} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </QueryState>
    </Card>
  )
}

/** 줄 안의 신원 인증 — 처리기·상태·심사 답. */
function KycSummary({ kyc }: { kyc: SignupKyc | null }) {
  if (!kyc) return <span className="text-xs text-slate-500">None (filed before the identity check at sign-up)</span>
  return (
    <span className="flex flex-wrap items-center gap-1">
      <Labeled labels={SIGNUP_KYC_STATUS_LABEL} code={kyc.status} />
      <span className="text-xs text-slate-500">· <Labeled labels={KYC_PROVIDER_LABEL} code={kyc.provider} /></span>
      {kyc.reviewAnswer && <Code>{kyc.reviewAnswer}</Code>}
      {kyc.reviewRejectType && <Code>{kyc.reviewRejectType}</Code>}
    </span>
  )
}

/* ── 상세 (XM-31 · XM-123 · XM-124 · XM-32 · XM-33) ───────────────── */

export function KycVerificationDetailPage() {
  const { applicationId } = useParams()
  const id = Number(applicationId)
  const q = useQuery({ queryKey: ['signup', id], queryFn: () => getSignup(id), enabled: Number.isFinite(id) && id > 0 })
  const a = q.data

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm"><Link className={LINK} to="/kyc-verification">← KYC verification</Link></p>
      <Card title="Sign-up application">
        <QueryState isPending={q.isPending} error={q.error}>
          {a && (
            <dl className="grid grid-cols-[12rem_1fr] gap-2 text-sm">
              <Row label="ID">{a.loginId}</Row>
              <Row label="Email"><Maybe value={a.email} /></Row>
              <Row label="Referral code"><Maybe value={a.referralCode} /></Row>
              <Row label="Status"><Labeled labels={SIGNUP_STATUS_LABEL} code={a.status} /></Row>
              <Row label="Applied at"><Time ms={a.createdAt} /></Row>
              <Row label="Manual review requested at"><Time ms={a.manualReviewRequestedAt} /></Row>
              <Row label="Decided at"><Time ms={a.reviewedAt} /></Row>
              <Row label="Approved by">
                {a.approvedBy ? APPROVED_BY_LABEL[a.approvedBy] : <Maybe value={null} />}
              </Row>
              <Row label="Rejection reason"><Maybe value={a.rejectReason} /></Row>
              <Row label="Member">
                {a.memberId ? <MemberLink memberId={a.memberId} loginId={a.loginId} /> : <Maybe value={null} />}
              </Row>
            </dl>
          )}
        </QueryState>
      </Card>
      {a && <IdentityCheckCard application={a} />}
      {a && a.kyc && <ResultsCard application={a} />}
      {a && a.kyc && <DocumentsCard applicationId={a.applicationId} />}
      {a && <DecisionCard application={a} />}
    </div>
  )
}

function IdentityCheckCard({ application }: { application: SignupApplication }) {
  const k = application.kyc
  return (
    <Card title="Identity check (SUMSUB)">
      {!k
        ? <Notice tone="warn">This application has no identity check — it was filed before the identity check at sign-up.</Notice>
        : (
          <dl className="grid grid-cols-[12rem_1fr] gap-2 text-sm">
            <Row label="Status"><Labeled labels={SIGNUP_KYC_STATUS_LABEL} code={k.status} /></Row>
            <Row label="Provider"><Labeled labels={KYC_PROVIDER_LABEL} code={k.provider} /></Row>
            {/* SUMSUB 대시보드에서 찾을 값 — 신청자 번호와 이 가입의 SUMSUB 사용자 번호(externalUserId). */}
            <Row label="SUMSUB applicant ID"><span className="font-mono text-xs"><Maybe value={k.applicantId} /></span></Row>
            <Row label="SUMSUB user ID"><span className="font-mono text-xs"><Maybe value={k.externalUserId} /></span></Row>
            <Row label="SUMSUB review status"><Maybe value={k.reviewStatus} /></Row>
            <Row label="Review result">
              <span className="flex flex-wrap gap-1">
                {k.reviewAnswer || k.reviewRejectType || (k.rejectLabels?.length ?? 0) > 0
                  ? <>
                      {k.reviewAnswer && <Code>{k.reviewAnswer}</Code>}
                      {k.reviewRejectType && <Code>{k.reviewRejectType}</Code>}
                      {(k.rejectLabels ?? []).map((l) => <Code key={l}>{l}</Code>)}
                    </>
                  : <Maybe value={null} />}
              </span>
            </Row>
            <Row label="Comment for the applicant"><Maybe value={k.moderationComment} /></Row>
            <Row label="Comment for us only"><Maybe value={k.clientComment} /></Row>
            <Row label="Started at"><Time ms={k.startedAt} /></Row>
            <Row label="Reviewed at"><Time ms={k.reviewedAt} /></Row>
            <Row label="Updated at"><Time ms={k.updatedAt} /></Row>
          </dl>
        )}
    </Card>
  )
}

function ResultsCard({ application }: { application: SignupApplication }) {
  const results = application.results ?? []
  return (
    <Card title="SUMSUB results received">
      {results.length === 0
        ? <p className="text-sm text-slate-500">No result from SUMSUB yet.</p>
        : (
          <table className="w-full text-sm">
            <thead><tr>
              <th className={TH}>Received at</th><th className={TH}>Way</th><th className={TH}>Type</th>
              <th className={TH}>Answer</th><th className={TH}>SUMSUB time</th><th className={TH}>Outcome</th>
            </tr></thead>
            <tbody>
              {results.map((r, i) => (
                <tr key={i}>
                  <td className={TD}><Time ms={r.receivedAt} /></td>
                  <td className={TD}>{r.source === 'WEBHOOK' ? 'Webhook' : 'Status check'}</td>
                  <td className={TD}><Maybe value={r.type} /></td>
                  <td className={TD}>
                    {r.reviewAnswer ? <Code>{r.reviewAnswer}</Code> : <Maybe value={null} />}
                    {r.reviewRejectType && <> <Code>{r.reviewRejectType}</Code></>}
                  </td>
                  <td className={TD}><Maybe value={r.sumsubTime} /></td>
                  <td className={TD}><Code>{r.outcome}</Code></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
    </Card>
  )
}

/** XM-123 — 단계마다, 사진은 [View] 를 눌러야 불러온다(XM-124). */
function DocumentsCard({ applicationId }: { applicationId: number }) {
  const q = useQuery({ queryKey: ['signup-documents', applicationId], queryFn: () => getSignupDocuments(applicationId) })
  return (
    <Card title="Submitted documents">
      <QueryState isPending={q.isPending} error={q.error}>
        {q.data && (q.data.available
          ? (q.data.steps.length === 0
            ? <p className="text-sm text-slate-500">Nothing was submitted.</p>
            : <div className="flex flex-col gap-4">
                {q.data.steps.map((s) => <StepBlock key={s.step} applicationId={applicationId} step={s} />)}
              </div>)
          : <Notice tone="info">{q.data.reason ?? 'Nothing to show.'}</Notice>)}
      </QueryState>
    </Card>
  )
}

function StepBlock({ applicationId, step }: { applicationId: number; step: KycStep }) {
  return (
    <section className="rounded border border-slate-200 p-3 dark:border-slate-700">
      <h3 className="mb-2 text-sm font-semibold">
        {step.step}
        {step.idDocType && <span className="ml-2 font-normal text-slate-500">{step.idDocType}</span>}
        {step.country && <span className="ml-2 font-normal text-slate-500">{step.country}</span>}
      </h3>
      <p className="mb-2 flex flex-wrap gap-1 text-xs">
        {step.reviewAnswer && <Code>{step.reviewAnswer}</Code>}
        {step.reviewRejectType && <Code>{step.reviewRejectType}</Code>}
        {(step.rejectLabels ?? []).map((l) => <Code key={l}>{l}</Code>)}
      </p>
      {step.moderationComment && <p className="text-xs text-slate-600 dark:text-slate-300">For the applicant: {step.moderationComment}</p>}
      {step.clientComment && <p className="text-xs text-slate-600 dark:text-slate-300">For us only: {step.clientComment}</p>}
      <div className="mt-2 flex flex-wrap gap-3">
        {step.images.length === 0
          ? <span className="text-xs text-slate-500">No image.</span>
          : step.images.map((img, i) => (
            <DocumentImage key={img.imageId} applicationId={applicationId} image={img}
              caption={i === 0 && step.images.length > 1 ? 'Front' : i > 0 ? 'Back' : 'Image'} />
          ))}
      </div>
    </section>
  )
}

function DocumentImage({ applicationId, image, caption }: { applicationId: number; image: KycImage; caption: string }) {
  const [src, setSrc] = useState<string | null>(null)
  const [state, setState] = useState<'idle' | 'loading' | 'failed'>('idle')
  useEffect(() => () => { if (src) URL.revokeObjectURL(src) }, [src])

  async function view() {
    setState('loading')
    try {
      setSrc(await fetchSignupDocument(applicationId, image.imageId))
      setState('idle')
    } catch {
      setState('failed')
    }
  }

  return (
    <figure className="w-64">
      <figcaption className="mb-1 flex flex-wrap items-center gap-1 text-xs text-slate-600 dark:text-slate-300">
        {caption} <span className="font-mono">#{image.imageId}</span>
        {image.reviewAnswer && <Code>{image.reviewAnswer}</Code>}
        {(image.rejectLabels ?? []).map((l) => <Code key={l}>{l}</Code>)}
      </figcaption>
      {src
        ? <a href={src} target="_blank" rel="noreferrer">
            <img src={src} alt={`${caption} ${image.imageId}`} className="max-w-full rounded border border-slate-200 dark:border-slate-700" />
          </a>
        : <Button tone="plain" onClick={() => { void view() }} disabled={state === 'loading'}>
            {state === 'loading' ? 'Loading…' : 'View'}
          </Button>}
      {state === 'failed' && <div className="mt-1"><Notice tone="stop">Could not load the image.</Notice></div>}
      {image.clientComment && <p className="mt-1 text-xs text-slate-500">For us only: {image.clientComment}</p>}
    </figure>
  )
}

/** XM-32 · XM-33 — 수동심사만. 다른 상태는 누가 정하는지 한 줄. */
function DecisionCard({ application }: { application: SignupApplication }) {
  const { has } = useSession()
  const qc = useQueryClient()
  const [mode, setMode] = useState<'approve' | 'reject' | null>(null)
  const w = useWrite()

  if (application.status !== 'MANUAL_REVIEW') {
    const line: Record<Exclude<SignupStatus, 'MANUAL_REVIEW'>, string> = {
      KYC_REVIEW: 'SUMSUB is still checking this identity check. When it passes, the sign-up is approved by itself.',
      KYC_REJECTED: 'SUMSUB finally rejected the identity check. It comes here when the applicant requests a manual review.',
      APPROVED: 'This sign-up is approved.',
      REJECTED: 'This sign-up was rejected. The applicant can sign up again from the identity check.',
    }
    return <Card title="Decision"><p className="text-sm text-slate-600 dark:text-slate-300">{line[application.status]}</p></Card>
  }

  // ⚠ 버튼 키 그대로(명세 0-5, 계약 7절 `signups.review`). 없으면 단추 자체가 없다.
  if (!has('signups.review')) {
    return <Card title="Decision"><p className="text-sm text-slate-600 dark:text-slate-300">You do not have permission to review sign-ups.</p></Card>
  }

  function close() { setMode(null); w.setError(null) }

  async function submit() {
    const approve = mode === 'approve'
    const send = async () => {
      if (approve) {
        const r = await approveSignup(application.applicationId)
        setFlash({
          tone: 'ok',
          text: `Approved the sign-up of ${application.loginId}.` + (r.referralResult ? ` (${r.referralResult})` : ''),
        })
        await qc.invalidateQueries({ queryKey: ['members'] })
      } else {
        await rejectSignup(application.applicationId)
        setFlash({ tone: 'ok', text: `Rejected the sign-up of ${application.loginId}. They can sign up again from the identity check.` })
      }
      await qc.invalidateQueries({ queryKey: ['signups'] })
      await qc.invalidateQueries({ queryKey: ['signup', application.applicationId] })
    }
    await submitWrite(w, approve ? 'Approve sign-up' : 'Reject sign-up', send, close)
  }

  return (
    <Card title="Decision">
      <p className="mb-3 text-sm text-slate-600 dark:text-slate-300">
        The applicant requested a manual review. Check the identity check and the submitted documents, then decide.
      </p>
      <div className="flex gap-2">
        <Button onClick={() => setMode('approve')}>Approve</Button>
        <Button tone="plain" onClick={() => setMode('reject')}>Reject</Button>
      </div>
      <Confirm
        open={mode !== null}
        title={mode === 'approve' ? 'Approve sign-up' : 'Reject sign-up'}
        action={mode === 'approve' ? 'Approve' : 'Reject'}
        busy={w.busy} error={w.error} onCancel={close} onConfirm={() => { void submit() }}
      >
        <dl className="grid grid-cols-[6rem_1fr] gap-1.5">
          <dt className="text-slate-500">ID</dt><dd>{application.loginId}</dd>
          <dt className="text-slate-500">Email</dt><dd>{application.email}</dd>
          <dt className="text-slate-500">Referral code</dt><dd><Maybe value={application.referralCode || null} /></dd>
        </dl>
        <p className="mt-3 text-xs text-slate-500">
          {mode === 'approve'
            ? 'Approving makes the account now and sends the approval mail. If there is a referral code, it moves on to a special member request.'
            : 'Rejecting closes the application and sends the rejection mail. To sign up again, the applicant starts over from the identity check.'}
        </p>
      </Confirm>
    </Card>
  )
}
